<template>
  <div :class="['input-container', type]">
    <div class="input-wrapper">
      <input
        :id="name"
        :type="setInputType"
        v-model="computedValue"
        class="input"
        :placeholder="placeholder"
        :disabled="isDisabled"
        @focus="emit('inputFocus')"
        @blur="emit('inputBlur')"
      />
      <label class="floating-label" :for="name">{{ placeholder }}</label>
      <UiIcon
        v-if="type === 'filter'"
        name="search"
        size="20px"
        class="filter-icon"
        color-fill="var(--state-color)"
      />
      <UiButton
        v-if="type === 'search'"
        :type="BtnType.TRANSPARENT"
        :icons="closeIcon"
        :is-disabled="isDisabled"
        aria-label="Close"
        @clicked="close"
        class="close-button"
      />
    </div>
    <UiButton
      v-if="type === 'search'"
      :type="BtnType.SECONDARY"
      :icons="ctaIcon"
      :is-disabled="isDisabled"
      :text="ctaButton.text"
      :url="ctaButton.url"
      :aria-label="ctaButton.text || 'Submit'"
      @clicked="submit"
      class="cta-button"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import type { WritableComputedRef } from 'vue'
import type { InputTypes, CtaButton } from './types'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'
import type { PropsIcon } from '@sephora-asia/ui-button/types'
import { BtnType } from '@sephora-asia/ui-button/types'
import UiButton from '@sephora-asia/ui-button/UiButton.vue'

interface Props {
  value: string | number
  type: InputTypes
  name: string
  placeholder: string
  isDisabled?: boolean
  ctaButton?: CtaButton
}
const props = withDefaults(defineProps<Props>(), {
  isDisabled: false,
  ctaButton: () => ({ icon: '', text: '', url: '' }),
})

const emit = defineEmits<{
  (e: 'input', value: string | number): void
  (e: 'inputFocus'): void
  (e: 'inputBlur'): void
  (e: 'close'): void
  (e: 'submit'): void
}>()

const setInputType = computed<string>(() => {
  if (['filter', 'search'].includes(props.type)) {
    return 'text'
  }
  return props.type
})

const closeIcon = computed<PropsIcon>(() => {
  return {
    left: {
      name: 'close',
      colorFill: 'var(--color-base-black)',
      size: '16px',
    },
  }
})

const ctaIcon = computed<PropsIcon>(() => {
  return {
    left: {
      name: props.ctaButton.icon || '',
      colorFill: 'var(--color-base-white)',
      size: '24px',
    },
  }
})

const newValue = ref<string | number>(props.value)
const computedValue: WritableComputedRef<string | number> = computed({
  get(): string | number {
    return newValue.value
  },
  set(value: string | number): void {
    newValue.value = value
    emit('input', value)
  },
})

watch(
  () => props.value,
  (propsVal: string | number) => {
    newValue.value = propsVal
  }
)

function close() {
  newValue.value = ''
  emit('input', '')
  emit('close')
}

function submit() {
  emit('submit')
}
</script>

<style lang="scss" scoped>
.input-container {
  --state-color: var(--color-base-grey-30);
  display: flex;
  align-items: center;

  &:not(.search) .input {
    border-top-right-radius: var(--size-radius-medium);
    border-bottom-right-radius: var(--size-radius-medium);
  }

  .floating-label {
    opacity: 0;
  }

  &:not(.search, .filter) {
    .floating-label {
      opacity: 1;
    }

    .input {
      padding-top: 12px;

      &::placeholder {
        opacity: 0;
      }
    }
  }

  &.search {
    .input {
      border-right: 0;
    }
  }
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;

  .filter-icon,
  .close-button {
    position: absolute;
    right: 12px;
  }

  .close-button {
    :deep(.btn-content) {
      padding: 0;
    }
  }
}

.input {
  @extend .body-m;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding-left: 12px;
  padding-right: 12px;
  background: var(--color-base-white);
  border: 1px solid var(--state-color);
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  border-top-left-radius: var(--size-radius-medium);
  border-bottom-left-radius: var(--size-radius-medium);
  color: var(--color-base-black);

  &:focus {
    outline: none;
  }

  &:disabled {
    --state-color: var(--color-base-grey-30);
    background-color: var(--color-base-grey-10);
  }

  &:focus,
  &:not(:placeholder-shown) {
    --state-color: var(--color-base-black);
    & + .floating-label {
      @extend .body-s;
      top: 0;
    }

    & ~ :deep(.filter-icon) svg,
    & ~ .close-button :deep(.close-icon) svg {
      fill: var(--color-base-black);
    }
  }

  &:placeholder-shown {
    & ~ .close-button {
      opacity: 0;
      visibility: hidden;
    }
  }
}

.floating-label {
  @extend .body-m;
  color: var(--color-base-grey-50);
  transition: 0.2s ease all;
  position: absolute;
  top: 8px;
  left: 12px;
  pointer-events: none;
}

.ui-button {
  padding: 0;

  .icon-container {
    cursor: pointer;
  }
}

.cta-button {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  :deep(.btn-content) {
    padding-left: 8px;
    padding-right: 8px;
  }
}
</style>
